// FileTableAgGridV2.tsx
import { Alert, Badge, Box, Button, Flex, Loader } from "@mantine/core";
import React, { useCallback, useMemo, useRef } from "react";

import { IFileAgGrid, IValueLabel } from "@mctypes/global.types";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, IsRowSelectable } from "ag-grid-community";
import { formatShortDate } from "../../app/routes/admin/selectors";
import { AG_GRID_LOCALE_NOR } from "../../agGridLocale";

type Props = {
  files: IFileAgGrid[];
  getSelectedFilesCb: (files: IFileAgGrid[]) => void;
  filterExtension?: IValueLabel;
  isLoading?: boolean;
};

const FileTableAgGrid: React.FC<Props> = ({ files, getSelectedFilesCb, filterExtension, isLoading }) => {
  const gridRef = useRef<AgGridReact<IFileAgGrid>>(null);

  const filteredRows = useMemo<IFileAgGrid[]>(() => {
    if (!files) return [];
    if (!filterExtension?.value) return files;
    const ext = filterExtension.value.toUpperCase();
    return files.filter((f) => f.name?.toUpperCase().endsWith(ext));
  }, [files, filterExtension]);

  const noFilesWithGivenFilter = !!filterExtension?.value && filteredRows.length === 0;

  const onSendSelected = useCallback(() => {
    const selected = gridRef.current?.api?.getSelectedRows?.() ?? [];
    getSelectedFilesCb(selected);
    gridRef.current?.api?.deselectAll?.();
  }, [getSelectedFilesCb]);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "name",
        headerName: "Navn",
        filter: true,
        width: 400,
        resizable: true,
        // ✅ Låt kolumnen rita rutorna
        checkboxSelection: true,
        headerCheckboxSelection: true,
        showDisabledCheckboxes: true,
      },
      {
        field: "lastUpdated",
        headerName: "Sist endret",
        filter: true,
        type: ["dateColumn"],
        valueFormatter: (params) => formatShortDate(params.value),
      },
    ],
    [],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      suppressMovable: true,
    }),
    [],
  );

  const isRowSelectable = useMemo<IsRowSelectable>(() => {
    return (params) => params?.data?.disabled !== true;
  }, []);

  return (
    <Flex className="ag-theme-alpine" direction="column" h="100%" style={{ overflow: "auto" }} w="100%">
      <Flex style={{ height: 40 }}>
        {filterExtension?.value && (
          <Badge
            mb="0.25rem"
            mt="0.25rem"
            color="gray"
            variant="light"
            leftSection={<i className="fa fa-filter fa-fw" />}
            style={{ display: "flex", alignItems: "center" }}
          >
            <b>{filterExtension.value}</b>
          </Badge>
        )}
        {isLoading === true && (
          <div style={{ marginLeft: "0.5rem", color: "#777", display: "flex", alignItems: "center" }}>
            <Loader mr="0.5rem" size="xs" />
            <span>Henter filer...</span>
          </div>
        )}
        <Box flex={1} />

        <Button mb="0.25rem" mt="0.25rem" onClick={onSendSelected} title="Send valgte filer">
          <i className="fa fa-angle-double-right" />
        </Button>
      </Flex>

      <Box flex={1} />

      {isLoading === true ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", color: "#777" }}>
          <Loader mr="0.5rem" size="sm" />
          <span>Henter filer...</span>
        </div>
      ) : noFilesWithGivenFilter ? (
        <Alert color="blue" variant="light">
          Ingen {filterExtension?.label}-filer i denne mappen
        </Alert>
      ) : (
        <AgGridReact<IFileAgGrid>
          ref={gridRef}
          rowData={filteredRows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows={true}
          // ✅ Klassisk API (hindrar ekstra urvalskolumn i nyere AG Grid)
          rowSelection="multiple"
          suppressRowClickSelection={true}
          isRowSelectable={isRowSelectable}
          localeText={AG_GRID_LOCALE_NOR}
        />
      )}
    </Flex>
  );
};

export default FileTableAgGrid;
